import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert, Button, Form, Modal, Spinner,
} from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import {
  SaleAnswerRow,
  SaleAnswersResponse,
  SelectedSaleAnswer,
} from '../types/SalesTypes';
import { api } from '../../../../../api';
import {
  MODAL_SALE_COLUMNS,
  SALE_EXPORT_CSV_HEADERS,
  buildEpisodeSelectableKeyMap,
  buildFlattenedRows,
  buildSaleExportRows,
  defaultColumnWidths,
  filterSaleAnswersForModal,
  getSelectableKeys,
  isCsvFilename,
  matchSaleImportIds,
  mergeSelectionFromModal,
  parseSaleImportCsv,
  saleExportFilename,
  selectedToDiscountMap,
  selectedToKeySet,
} from '../salesUtils';
import { TriStateCheckbox } from './TriStateCheckbox';
import { WindowedSaleTable } from './WindowedSaleTable';
import styles from './SelectAnswersModal.module.scss';

interface SelectAnswersModalProps {
  show: boolean;
  promotionId: number;
  initialSelection: SelectedSaleAnswer[];
  onHide: () => void;
  onConfirm: (selection: SelectedSaleAnswer[]) => void;
}

export function SelectAnswersModal(props: SelectAnswersModalProps) {
  const {
    show,
    promotionId,
    initialSelection,
    onHide,
    onConfirm,
  } = props;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<SaleAnswerRow[]>([]);
  const [storyTitle, setStoryTitle] = useState('');
  const [storyUuid, setStoryUuid] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => selectedToKeySet(initialSelection));
  const [showUnreleased, setShowUnreleased] = useState(false);
  const [columnWidths, setColumnWidths] = useState(() => defaultColumnWidths(MODAL_SALE_COLUMNS));
  const [importErrorMessage, setImportErrorMessage] = useState<string | null>(null);
  const [importMissingIds, setImportMissingIds] = useState<string[]>([]);
  const initialSelectionRef = useRef(initialSelection);
  initialSelectionRef.current = initialSelection;
  const importInputRef = useRef<HTMLInputElement>(null);

  const visibleAnswers = useMemo(
    () => filterSaleAnswersForModal(answers, showUnreleased),
    [answers, showUnreleased],
  );

  const selectableKeys = useMemo(() => getSelectableKeys(visibleAnswers), [visibleAnswers]);

  const flattenedRows = useMemo(() => buildFlattenedRows(visibleAnswers), [visibleAnswers]);

  const exportRows = useMemo(
    () => buildSaleExportRows(storyTitle, visibleAnswers),
    [storyTitle, visibleAnswers],
  );

  const episodeSelectableKeys = useMemo(
    () => buildEpisodeSelectableKeyMap(visibleAnswers),
    [visibleAnswers],
  );

  const missingAnswerIndexCount = useMemo(
    () => visibleAnswers.filter((row) => row.answerIndex == null).length,
    [visibleAnswers],
  );

  const selectAllState = useMemo(() => {
    if (selectableKeys.length === 0) {
      return { checked: false, indeterminate: false };
    }
    let selectedCount = 0;
    for (const key of selectableKeys) {
      if (selectedKeys.has(key)) {
        selectedCount += 1;
      }
    }
    if (selectedCount === 0) {
      return { checked: false, indeterminate: false };
    }
    if (selectedCount === selectableKeys.length) {
      return { checked: true, indeterminate: false };
    }
    return { checked: false, indeterminate: true };
  }, [selectableKeys, selectedKeys]);

  const loadAnswers = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get<SaleAnswersResponse>(`/v1/tools/promotions/${promotionId}/sale-answers`)
      .then((response) => {
        setAnswers(response.data.answers);
        setStoryTitle(response.data.storyTitle);
        setStoryUuid(response.data.storyUuid);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || 'Failed to load sale answers');
        setAnswers([]);
      })
      .finally(() => setLoading(false));
  }, [promotionId]);

  useEffect(() => {
    if (!show) {
      return;
    }
    setSelectedKeys(selectedToKeySet(initialSelectionRef.current));
    setShowUnreleased(false);
    setColumnWidths(defaultColumnWidths(MODAL_SALE_COLUMNS));
    loadAnswers();
  }, [show, promotionId, loadAnswers]);

  const handleSelectAll = (checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const key of selectableKeys) {
          next.add(key);
        }
      } else {
        for (const key of selectableKeys) {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const handleToggleRow = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const handleToggleEpisode = (episodeUuid: string, checked: boolean) => {
    const keys = episodeSelectableKeys.get(episodeUuid) ?? [];
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const key of keys) {
          next.add(key);
        }
      } else {
        for (const key of keys) {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const handleColumnWidthChange = (columnId: string, width: number) => {
    setColumnWidths((prev) => ({ ...prev, [columnId]: width }));
  };

  const closeImportError = () => {
    setImportErrorMessage(null);
    setImportMissingIds([]);
  };

  const showImportError = (message: string, missingIds: string[] = []) => {
    setImportErrorMessage(message);
    setImportMissingIds(missingIds);
  };

  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  const handleImportFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    if (!isCsvFilename(file.name)) {
      showImportError('Please choose a CSV file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      const parsed = parseSaleImportCsv(text);
      if (parsed.error) {
        showImportError(parsed.error);
        return;
      }

      const { keysToSelect, missingIds } = matchSaleImportIds(answers, parsed.markedAnswerIds);
      setSelectedKeys(new Set(keysToSelect));

      const notFound = [
        ...parsed.invalidAnswerIds,
        ...missingIds.map((id) => String(id)),
      ];
      if (notFound.length > 0) {
        showImportError(
          'The following answer IDs from the CSV could not be found in this book:',
          notFound,
        );
      }
    };
    reader.onerror = () => {
      showImportError('The CSV file could not be read.');
    };
    reader.readAsText(file);
  };

  const handleNext = () => {
    const existingByKey = selectedToDiscountMap(initialSelection);
    const merged = mergeSelectionFromModal(answers, selectedKeys, existingByKey);
    onConfirm(merged);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      dialogClassName={styles.modalDialog}
      contentClassName={styles.modalShell}
    >
      <Modal.Header closeButton>
        <Modal.Title>Select Answers</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalContent}>
        <div className={styles.modalHeaderRow}>
          <div>
            <div className="font-weight-bold">{storyTitle || '—'}</div>
            <div className={styles.storyUuid}>{storyUuid || '—'}</div>
            <Form.Check
              type="checkbox"
              id="sale-show-unreleased"
              className={styles.showUnreleased}
              label="Show unreleased episodes"
              checked={showUnreleased}
              onChange={(event) => setShowUnreleased(event.target.checked)}
              disabled={loading || Boolean(error)}
            />
          </div>
          <div className={styles.headerActions}>
            <Button
              variant="outline-primary"
              as={CSVLink}
              data={exportRows}
              headers={[...SALE_EXPORT_CSV_HEADERS]}
              filename={saleExportFilename(storyTitle)}
              disabled={loading || Boolean(error) || visibleAnswers.length === 0}
            >
              Export CSV
            </Button>
            <Button
              variant="outline-primary"
              onClick={handleImportClick}
              disabled={loading || Boolean(error) || answers.length === 0}
            >
              Import CSV
            </Button>
            <input
              ref={importInputRef}
              type="file"
              accept=".csv,text/csv"
              className={styles.hiddenFileInput}
              onChange={handleImportFile}
            />
          </div>
        </div>

        {missingAnswerIndexCount > 0 && !loading && (
          <Alert variant="warning" className="py-2">
            {missingAnswerIndexCount}
            {' '}
            priced answer(s) are missing an answer index and cannot be selected.
          </Alert>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <div className={styles.modalBody}>
            {!error && answers.length === 0 ? (
              <Alert variant="info" className="mb-0">No priced choice answers found for this book.</Alert>
            ) : !error && visibleAnswers.length === 0 ? (
              <Alert variant="info" className="mb-0">
                No released episodes with priced answers. Check Show unreleased episodes to include them.
              </Alert>
            ) : (
              <WindowedSaleTable
                flattenedRows={flattenedRows}
                columns={MODAL_SALE_COLUMNS}
                columnWidths={columnWidths}
                onColumnWidthChange={handleColumnWidthChange}
                selectedKeys={selectedKeys}
                episodeSelectableKeys={episodeSelectableKeys}
                onToggleRow={handleToggleRow}
                onToggleEpisode={handleToggleEpisode}
                saleColumnHeaderSlot={(
                  <TriStateCheckbox
                    id="sale-select-all"
                    checked={selectAllState.checked}
                    indeterminate={selectAllState.indeterminate}
                    onChange={handleSelectAll}
                    disabled={selectableKeys.length === 0}
                  />
                )}
              />
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          className={styles.nextButton}
          onClick={handleNext}
          disabled={loading || Boolean(error)}
        >
          Next
        </Button>
      </Modal.Footer>
      {importErrorMessage && (
        <div
          className={styles.importErrorOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="sale-import-error-title"
        >
          <div className={styles.importErrorBox}>
            <h5 id="sale-import-error-title" className={styles.importErrorTitle}>
              Import CSV Error
            </h5>
            <p className="mb-2">{importErrorMessage}</p>
            {importMissingIds.length > 0 && (
              <pre className={styles.missingIdList}>
                {importMissingIds.join('\n')}
              </pre>
            )}
            <div className={styles.importErrorActions}>
              <Button variant="primary" onClick={closeImportError}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
